import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Copy, Check, Trash2, Key, X } from 'lucide-react';
import Breadcrumb from '../../components/UI/Breadcrumb/Breadcrumb';
import './ApiKeys.css';

const maskKey = (key) => {
  if (!key || key.length <= 10) return key;
  return key.slice(0, 8) + '••••••••' + key.slice(-4);
};

const ApiKeys = () => {
  const breadcrumbItems = [
    { label: '首页', path: '/dashboard' },
    { label: '管理API密钥', path: null },
  ];

  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeyForm, setNewKeyForm] = useState({ name: '', description: '' });
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [toast, setToast] = useState(null);
  const confirmTimerRef = useRef(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  }, []);

  const fetchApiKeys = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/appkeys', { credentials: 'include' });
      const data = await response.json();
      if (data.code === 0 && data.data?.appKeys) {
        setApiKeys(data.data.appKeys);
      } else {
        setApiKeys([]);
      }
    } catch {
      setApiKeys([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  const handleCopy = useCallback(async (key, id) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopiedId(id);
      showToast('已复制到剪贴板');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      showToast('复制失败，请手动复制', 'error');
    }
  }, [showToast]);

  const handleDeleteClick = useCallback((id) => {
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    setConfirmDeleteId(id);
    confirmTimerRef.current = setTimeout(() => setConfirmDeleteId(null), 3000);
  }, []);

  const handleDeleteConfirm = useCallback(async (id) => {
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    setConfirmDeleteId(null);
    try {
      const response = await fetch(`/api/appkeys/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await response.json();
      if (data.code === 0) {
        await fetchApiKeys();
        showToast('密钥已删除');
      } else {
        showToast(data.msg || '删除失败', 'error');
      }
    } catch {
      showToast('网络错误，请稍后重试', 'error');
    }
  }, [fetchApiKeys, showToast]);

  const handleCreateKey = useCallback(async () => {
    if (!newKeyForm.name.trim()) {
      showToast('请输入密钥名称', 'error');
      return;
    }
    try {
      setCreating(true);
      const response = await fetch('/api/appkeys', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newKeyForm),
      });
      const data = await response.json();
      if (data.code === 0) {
        await fetchApiKeys();
        setNewKeyForm({ name: '', description: '' });
        setShowCreateForm(false);
        showToast('API密钥创建成功');
      } else {
        showToast(data.msg || '创建失败', 'error');
      }
    } catch {
      showToast('网络错误，请稍后重试', 'error');
    } finally {
      setCreating(false);
    }
  }, [newKeyForm, fetchApiKeys, showToast]);

  return (
    <div className="apikeys-page">
      <Breadcrumb items={breadcrumbItems} />

      {/* Page header */}
      <div className="ak-header">
        <div className="ak-header-text">
          <h1 className="ak-title">管理API密钥</h1>
          <p className="ak-desc">通过创建API密钥为您的应用程序提供访问权限</p>
        </div>
        <button
          className="ak-create-btn"
          onClick={() => setShowCreateForm(v => !v)}
        >
          <Plus size={16} />
          新建密钥
        </button>
      </div>

      {/* Main card */}
      <div className="ak-card">

        {/* Inline create form */}
        {showCreateForm && (
          <div className="ak-form-panel">
            <div className="ak-form-header">
              <span className="ak-form-title">新建密钥</span>
              <button className="ak-form-close" onClick={() => setShowCreateForm(false)}>
                <X size={16} />
              </button>
            </div>
            <div className="ak-form-body">
              <div className="ak-form-row">
                <div className="ak-form-group">
                  <label className="ak-label">
                    密钥名称 <span className="ak-required">*</span>
                  </label>
                  <input
                    className="ak-input"
                    type="text"
                    placeholder="例：生产环境"
                    value={newKeyForm.name}
                    onChange={e => setNewKeyForm(p => ({ ...p, name: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && handleCreateKey()}
                  />
                </div>
                <div className="ak-form-group ak-form-group--flex">
                  <label className="ak-label">描述（可选）</label>
                  <input
                    className="ak-input"
                    type="text"
                    placeholder="简短描述这个密钥的用途"
                    value={newKeyForm.description}
                    onChange={e => setNewKeyForm(p => ({ ...p, description: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && handleCreateKey()}
                  />
                </div>
                <div className="ak-form-actions">
                  <button
                    className="ak-btn ak-btn--ghost"
                    onClick={() => { setShowCreateForm(false); setNewKeyForm({ name: '', description: '' }); }}
                  >
                    取消
                  </button>
                  <button
                    className="ak-btn ak-btn--primary"
                    onClick={handleCreateKey}
                    disabled={creating || !newKeyForm.name.trim()}
                  >
                    {creating ? '创建中…' : '创建密钥'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Table / states */}
        {loading ? (
          <div className="ak-loading">
            <div className="ak-spinner" />
            <span>加载中…</span>
          </div>
        ) : apiKeys.length === 0 ? (
          <div className="ak-empty">
            <div className="ak-empty-icon"><Key size={28} /></div>
            <p className="ak-empty-title">暂无API密钥</p>
            <p className="ak-empty-hint">创建一个API密钥，开始使用我们的API服务</p>
            <button className="ak-btn ak-btn--primary" onClick={() => setShowCreateForm(true)}>
              <Plus size={15} />
              新建第一个密钥
            </button>
          </div>
        ) : (
          <div className="ak-table-wrap">
            <table className="ak-table">
              <thead>
                <tr>
                  <th>名称</th>
                  <th>API密钥</th>
                  <th>描述</th>
                  <th>创建时间</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {apiKeys.map(key => (
                  <tr key={key.id}>
                    <td className="ak-td-name">{key.name}</td>
                    <td className="ak-td-key">
                      <span className="ak-key-mask">{maskKey(key.appKey)}</span>
                      <button
                        className={`ak-copy-btn${copiedId === key.id ? ' copied' : ''}`}
                        onClick={() => handleCopy(key.appKey, key.id)}
                        title="复制完整密钥"
                      >
                        {copiedId === key.id ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                    </td>
                    <td className="ak-td-desc">{key.description || '—'}</td>
                    <td className="ak-td-date">{key.createdAt}</td>
                    <td className="ak-td-actions">
                      {confirmDeleteId === key.id ? (
                        <span className="ak-confirm-row">
                          <button className="ak-btn-danger-confirm" onClick={() => handleDeleteConfirm(key.id)}>
                            确认删除
                          </button>
                          <button className="ak-btn-cancel" onClick={() => setConfirmDeleteId(null)}>
                            取消
                          </button>
                        </span>
                      ) : (
                        <button className="ak-delete-btn" onClick={() => handleDeleteClick(key.id)}>
                          <Trash2 size={14} />
                          删除
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className={`ak-toast ak-toast--${toast.type}`}>
          {toast.type === 'success' ? <Check size={15} /> : <X size={15} />}
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default ApiKeys;
